import { useEffect } from 'react';
import { getLastKeyboardInset, isIOS } from '@/common/keyboard/keyboardShared';

/**
 * 폰 화면 키보드와 글 입력칸 — 공용 신호 한 곳(UI 가이드 7.1).
 *
 * 1) `body[data-input-focused]` — 하단 탭·폼 하단 고정 줄([저장]·[이전][다음])을 숨기는 신호.
 *    "글 입력칸에 포커스가 있고 **화면 키보드가 실제로 떠 있을 때**"만 켠다. 포커스만 보면 이런 일이 났다(2026-10-06 앱 점검):
 *    - 직접 입력 칸에서 엔터 → 칩이 추가되며 입력칸이 DOM 에서 빠짐 → iOS 는 focusout 을 보내지 않아 키보드는 내려갔는데 줄이 숨은 채 남음
 *    - 안드로이드 뒤로 키로 키보드만 내림 → 포커스는 그대로라 줄이 숨은 채 남음
 *    그래서 포커스·키보드 높이(visualViewport·window 높이)·DOM 변화가 있을 때마다 다시 판정한다.
 * 2) 숨김 상태에서 시작한 터치가 끝나기 전에는 숨김을 풀지 않는다 — 숨은 줄이 그 터치 도중 다시 나타나면 iOS 가 그 탭을
 *    나타난 [저장]으로 보내 이력서가 저장됐다(2026-10-06 22:10:02, 이력서 수정 직접 입력 엔터 뒤 빈 곳 누름).
 * 3) 키보드가 올라와 보이는 영역이 줄어든 뒤 포커스된 칸(편집기는 커서)이 키보드나 머리줄·고정 탭 뒤에 있으면 보이는 영역 가운데로
 *    한 번만 옮긴다. 이미 보이면 건드리지 않는다(iOS 가 먼저 옮긴 경우 두 번 스크롤 금지). 시트(role=dialog)·스크롤 잠금 중에는 시트가 맡는다.
 * 5) `body[data-keyboard-up]` — 글 입력칸 포커스 + 키보드가 **실제로 떠 있을 때만**(1 의 포커스 직후 유예 없음). 하단 탭 자리로 남겨 둔
 *    푸터 아래 여백(84px + 안전 여백)을 이때만 걷는다 — 키보드가 뜬 채 맨 아래로 내리면 그 여백이 키보드 위 회색 빈 영역(약 110pt)으로
 *    보였다(2026-10-07 채용 등록, 새 앱). 포커스 직후(키보드가 올라오기 전)에 걷으면 맨 아래를 보던 화면에선 문서가 짧아진 만큼 스크롤이
 *    당겨져 칸이 튄다. 키보드가 뜬 뒤엔 보이는 영역 아래 끝이 문서 끝보다 키보드만큼 위라 걷어도 스크롤이 당겨지지 않는다.
 * 4) 아이폰: 키보드에 가릴 자리의 칸을 누르면(손을 뗄 때) 문서를 먼저 칸이 키보드 위에 오게 올린 뒤 직접 포커스를 준다 — 그냥 두면
 *    iOS 가 키보드가 올라오는 동안 보이는 화면을 밀어 올려, sticky 머리줄이 그 사이 화면 아래로 밀렸다가(y≈200→100→60) 미끄러져
 *    돌아왔다(2026-10-07 채용 등록 3단계 특별 복지 내용 칸, 새 앱 녹화). 칸이 가리지 않으면 건드리지 않는다. 시트 안은 시트가 맡는다.
 */

/** 화면이 이 높이 이상 줄면 화면 키보드로 본다(안드로이드·새 앱처럼 웹뷰 자체가 줄 때 — 주소창·도구 막대 변화 80px 안팎은 무시) */
const KEYBOARD_MIN_PX: number = 120;
/**
 * 웹뷰 위를 이 높이 이상 덮으면(innerHeight − visualViewport) 키보드로 본다 — iOS 는 화면 키보드뿐 아니라 하드웨어 키보드일 때의
 * 입력 도구 줄(68px)도 웹뷰를 덮는다. 그때 하단 탭을 띄우면 도구 줄 위로 떠 그 사이로 본문이 비친다(2026-10-06 시뮬레이터). 주소창 변화는 둘이 함께 줄어 0
 */
const OVERLAY_MIN_PX: number = 40;
/** 입력칸을 누른 뒤 키보드가 올라오는 동안 — 이 동안은 키보드를 아직 못 쟀어도 숨김(줄이 키보드와 함께 떠오르지 않게) */
const FOCUS_GRACE_MS: number = 800;
/** 숨김 상태에서 시작한 터치가 끝난 뒤 이만큼 더 숨김을 유지한다(iOS 가 탭을 늦게 확정하는 시간) */
const RELEASE_HOLD_MS: number = 450;
/** 포커스 뒤 이 시간 안에 보이는 영역이 줄면 칸이 보이는지 확인한다 */
const REVEAL_WINDOW_MS: number = 600;
/** 크기·스크롤 변화가 멈춘 뒤 확인하기까지 기다리는 시간(키보드 애니메이션·iOS 가 보이는 화면을 옮기는 것이 끝나게) */
const REVEAL_SETTLE_MS: number = 160;
/** 칸과 보이는 영역 끝 사이 여유 */
const REVEAL_GAP_PX: number = 8;
/** 누른 손가락이 이만큼 넘게 움직였으면 누르기가 아니라 끌기다 */
const TAP_SLOP_PX: number = 10;
/** 미리 올릴 때 칸 아래와 키보드 위 사이 여유 */
const PRE_RAISE_GAP_PX: number = 16;
/** 처음 열 때의 아이폰 키보드 높이 어림(입력 도구 줄 포함, 화면 높이의 46% 안팎) — 크게 잡아야 iOS 가 화면을 밀지 않는다 */
const IOS_KEYBOARD_RATIO: number = 0.46;

/** 글자 입력칸인지 — 체크박스·버튼·읽기 전용 같은 입력은 키보드를 올리지 않는다 */
export const isTextEntry = (element: Element | null): element is HTMLElement => {
    if (!(element instanceof HTMLElement)) return false;
    if (element.isContentEditable) return true;
    if (element instanceof HTMLTextAreaElement) return !element.readOnly && !element.disabled;
    if (!(element instanceof HTMLInputElement)) return false;
    if (element.readOnly || element.disabled) return false;
    return !['checkbox', 'radio', 'button', 'submit', 'reset', 'file', 'range', 'color', 'image', 'hidden'].includes(element.type);
};

/** DOM 에 붙어 있고 화면에 그려지는 칸인지 — 칩 추가로 빠졌거나 접혀 숨은 칸은 아니다 */
const isRendered = (element: HTMLElement): boolean => {
    if (!element.isConnected || element.getClientRects().length === 0) return false;
    return window.getComputedStyle(element).visibility !== 'hidden';
};

/** 지금 보이는 영역 높이(확대 배율을 걷어 낸 값) */
const viewportHeight = (): number => {
    const vv: VisualViewport | null = window.visualViewport;
    return vv ? vv.height * (vv.scale > 1.01 ? vv.scale : 1) : window.innerHeight;
};

/** 키보드 없을 때 높이 — 같은 폭에서 본 가장 큰 높이(폭이 바뀌면 다시 잰다: 화면 회전) */
const baseline: { width: number; height: number } = { width: 0, height: 0 };
const updateBaseline = (): void => {
    const height: number = Math.max(window.innerHeight, viewportHeight());
    if (baseline.width !== window.innerWidth) {
        baseline.width = window.innerWidth;
        baseline.height = height;
    } else if (height > baseline.height) {
        baseline.height = height;
    }
};

/** 키보드(화면 키보드, iOS 하드웨어 키보드의 입력 도구 줄 포함)가 떠 있는지 — iOS(웹뷰·사파리)는 visualViewport, 안드로이드·새 앱은 window 높이가 준다 */
export const isSoftKeyboardUp = (): boolean => {
    updateBaseline();
    if (window.innerHeight - viewportHeight() > OVERLAY_MIN_PX) return true;
    return baseline.height - viewportHeight() > KEYBOARD_MIN_PX;
};

/**
 * 지금 보이는 영역의 위·아래 끝 — 칸 위치(getBoundingClientRect)와 같은 좌표로.
 * 표준(크롬)은 칸 좌표가 레이아웃 화면 기준이라 visualViewport.offsetTop 만큼 아래가 보이는 영역이다. iOS 웹킷은 키보드가 뜨면 칸 좌표가
 * 이미 보이는 화면 기준이고, 대신 고정(fixed) 요소가 offsetTop 만큼 위로 밀린다(2026-10-06 시뮬레이터 측정 — offsetTop 229, 고정 기준점 -229).
 * 화면 맨 위 고정 기준점의 위치를 더해 둘을 맞춘다.
 */
const visibleBand = (): { top: number; bottom: number } => {
    const vv: VisualViewport | null = window.visualViewport;
    if (!vv) return { top: 0, bottom: window.innerHeight };
    const probe: HTMLDivElement = document.createElement('div');
    probe.style.cssText = 'position:fixed;top:0;left:0;width:0;height:0;visibility:hidden;pointer-events:none';
    document.body.appendChild(probe);
    const fixedTop: number = probe.getBoundingClientRect().top;
    probe.remove();
    const top: number = vv.offsetTop + fixedTop;
    return { top, bottom: top + vv.height };
};

/** 보이는 영역 맨 위를 가리는 고정 줄(머리줄·섹션 탭·단계 막대)의 아래 끝 — 화면 가운데 줄을 위에서부터 짚어 잰다(하드코딩 없음) */
const obstructedTop = (visibleTop: number, visibleBottom: number): number => {
    const x: number = window.innerWidth / 2;
    let top: number = visibleTop;
    for (let guard: number = 0; guard < 6; guard += 1) {
        const y: number = top + 1;
        if (y >= visibleBottom) break;
        let el: Element | null = document.elementFromPoint(x, y);
        let stuck: HTMLElement | null = null;
        while (el && el !== document.body && el !== document.documentElement) {
            const position: string = window.getComputedStyle(el).position;
            if (position === 'fixed' || position === 'sticky') stuck = el as HTMLElement;
            el = el.parentElement;
        }
        if (!stuck) break;
        const bottom: number = stuck.getBoundingClientRect().bottom;
        if (bottom <= top) break;
        top = bottom;
    }
    return top;
};

/** 칸(편집기는 커서 줄)의 화면 위치 */
const targetRect = (element: HTMLElement): DOMRect => {
    if (element.isContentEditable) {
        const selection: Selection | null = window.getSelection();
        if (selection && selection.rangeCount > 0 && element.contains(selection.anchorNode)) {
            const range: Range = selection.getRangeAt(0);
            const rect: DOMRect | undefined = range.getClientRects()[0] ?? range.getBoundingClientRect();
            if (rect && (rect.height > 0 || rect.top !== 0)) return rect;
        }
    }
    return element.getBoundingClientRect();
};

/** 문서가 아닌 안쪽 스크롤 칸에 들어 있는지 */
/** 칸이 화면에 고정된 줄(position fixed·sticky) 안에 있나 — 문서를 스크롤해도 그 칸은 움직이지 않는다 */
const inFixedLayer = (element: HTMLElement): boolean => {
    for (let node: HTMLElement | null = element; node && node !== document.body; node = node.parentElement) {
        const position: string = window.getComputedStyle(node).position;
        if (position === 'fixed' || position === 'sticky') return true;
    }
    return false;
};

const hasInnerScroller = (element: HTMLElement): boolean => {
    for (let el: HTMLElement | null = element.parentElement; el && el !== document.body; el = el.parentElement) {
        const overflowY: string = window.getComputedStyle(el).overflowY;
        if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight + 1) return true;
    }
    return false;
};

/** 포커스된 칸이 키보드·고정 줄 뒤에 있으면 보이는 영역 가운데로 한 번 옮긴다 */
export const revealFocusedEntry = (element: HTMLElement): void => {
    if (!element.isConnected || document.activeElement !== element) return;
    if (element.closest('[role="dialog"]') || document.body.dataset.scrollLock === 'true') return;
    // 아이폰 글쓰기 화면은 #root 안에서 커서를 직접 맞춘다(components/common/write/useWriteKeyboardFrame) — 여기서 편집기 통째를 가운데로 옮기면 커서가 벗어난다
    if (document.body.dataset.writeKeyboard === 'true') return;
    const { top: visibleTop, bottom: visibleBottom } = visibleBand();
    const top: number = obstructedTop(visibleTop, visibleBottom);
    const rect: DOMRect = targetRect(element);
    if (rect.top >= top && rect.bottom <= visibleBottom) return;
    const room: number = visibleBottom - top;
    // 칸이 보이는 영역보다 크면(긴 글 칸) 윗부분을 고정 줄 바로 아래에, 아니면 가운데에
    const delta: number = rect.height > room - REVEAL_GAP_PX * 2
        ? rect.top - top - REVEAL_GAP_PX
        : (rect.top + rect.bottom) / 2 - (top + visibleBottom) / 2;
    if (Math.abs(delta) < 1) return;
    if (hasInnerScroller(element)) {
        element.style.scrollMarginTop = `${Math.max(0, top - visibleTop) + REVEAL_GAP_PX}px`;
        element.scrollIntoView({ block: 'center' });
        return;
    }
    window.scrollBy({ top: delta, left: 0, behavior: 'auto' });
};

/**
 * Header 에서 한 번 건다 — 입력 중 하단 줄 숨김 신호·숨김 중 시작한 터치 보호·키보드에 가린 칸 보이기.
 */
export const useInputKeyboardSignal = (): void => {
    useEffect(() => {
        const body: HTMLElement = document.body;
        const coarse: boolean = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
        let focusedAt: number = 0;
        let keyboardWasUp: boolean = false;
        let gestureWhileHidden: boolean = false;
        let holdUntil: number = 0;
        let timer: number | undefined;
        let frame: number = 0;
        let observer: MutationObserver | null = null;
        let revealUntil: number = 0;
        let revealTimer: number | undefined;

        const isOn = (): boolean => body.dataset.inputFocused === 'true';
        const focusedEntry = (): HTMLElement | null => {
            const active: Element | null = document.activeElement;
            return isTextEntry(active) && isRendered(active) ? active : null;
        };
        const wanted = (): boolean => {
            if (!focusedEntry()) return false;
            if (isSoftKeyboardUp()) return true;
            return coarse && Date.now() - focusedAt < FOCUS_GRACE_MS;
        };
        const watchDom = (on: boolean): void => {
            if (on && !observer && typeof MutationObserver !== 'undefined') {
                observer = new MutationObserver(() => schedule());
                observer.observe(body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
            } else if (!on && observer) {
                observer.disconnect();
                observer = null;
            }
        };
        const sync = (): void => {
            window.clearTimeout(timer);
            timer = undefined;
            // 5) 키보드가 실제로 떠 있는 동안만(유예·터치 보호 없이 바로 따른다 — 여백이 돌아오는 건 문서가 길어질 뿐이라 튀지 않는다)
            const keyboardUp: boolean = !!focusedEntry() && isSoftKeyboardUp();
            if (keyboardUp && body.dataset.keyboardUp !== 'true') body.dataset.keyboardUp = 'true';
            else if (!keyboardUp && body.dataset.keyboardUp) delete body.dataset.keyboardUp;
            const on: boolean = wanted();
            if (on) {
                if (!isOn()) body.dataset.inputFocused = 'true';
                watchDom(true);
                // 키보드를 아직 못 잰 채 포커스만 있는 동안은 유예가 끝날 때 다시 본다(하드웨어 키보드면 거기서 꺼진다)
                if (!isSoftKeyboardUp() && coarse) timer = window.setTimeout(sync, Math.max(0, FOCUS_GRACE_MS - (Date.now() - focusedAt)) + 30);
                return;
            }
            if (!isOn()) {
                watchDom(false);
                return;
            }
            // 숨김 상태에서 시작한 터치가 끝나기 전에는 풀지 않는다(그 탭이 다시 나타난 버튼으로 가지 않게)
            const wait: number = gestureWhileHidden ? RELEASE_HOLD_MS : holdUntil - Date.now();
            if (wait > 0) {
                timer = window.setTimeout(sync, wait);
                return;
            }
            delete body.dataset.inputFocused;
            watchDom(false);
        };
        const schedule = (): void => {
            if (frame) return;
            frame = window.requestAnimationFrame(() => {
                frame = 0;
                sync();
            });
        };

        let revealPending: boolean = false;
        const reveal = (): void => {
            revealPending = true;
            window.clearTimeout(revealTimer);
            revealTimer = window.setTimeout(() => {
                revealPending = false;
                const entry: HTMLElement | null = focusedEntry();
                if (entry && isSoftKeyboardUp()) revealFocusedEntry(entry);
            }, REVEAL_SETTLE_MS);
        };
        // iOS 는 키보드가 뜬 뒤에도 한동안 보이는 화면을 옮긴다 — 확인 대기 중이면 그 움직임이 멈출 때까지 더 미룬다(두 번 스크롤·튐 방지)
        const onScrollWhilePending = (): void => {
            if (revealPending) reveal();
        };
        const onViewportChange = (): void => {
            const up: boolean = isSoftKeyboardUp();
            // 포커스 직후 줄어든 경우 + 이미 포커스된 칸을 다시 눌러 키보드가 다시 올라온 경우(안드로이드 뒤로 키 뒤)
            if (up && (Date.now() < revealUntil || !keyboardWasUp)) reveal();
            keyboardWasUp = up;
            sync();
        };
        const onFocusIn = (event: FocusEvent): void => {
            if (isTextEntry(event.target as Element | null)) {
                focusedAt = Date.now();
                revealUntil = focusedAt + REVEAL_WINDOW_MS;
                // 다른 칸으로 옮겨 갈 때처럼 키보드가 이미 떠 있으면 크기 변화가 없다 — 바로 확인한다
                if (isSoftKeyboardUp()) reveal();
            }
            sync();
        };
        // 포커스가 입력칸 사이를 옮겨 갈 때 잠깐 비는 순간에 줄이 깜빡이지 않게 한 박자 뒤에 본다
        const onFocusOut = (): void => {
            window.setTimeout(sync, 0);
        };
        const onPointerDown = (): void => {
            if (isOn()) gestureWhileHidden = true;
            window.setTimeout(sync, 0);
        };
        const onPointerEnd = (): void => {
            if (!gestureWhileHidden) return;
            gestureWhileHidden = false;
            holdUntil = Date.now() + RELEASE_HOLD_MS;
            sync();
        };
        // 앱을 나가면 입력칸을 내려 신호를 끈다 — 돌아왔을 때 키보드 없이 하단 탭·폼 저장 줄이 숨은 채 남지 않게(UI 가이드 7.1)
        const onHide = (event: Event): void => {
            if (event.type === 'visibilitychange' && document.visibilityState !== 'hidden') return;
            const active: Element | null = document.activeElement;
            if (isTextEntry(active)) active.blur();
            gestureWhileHidden = false;
            holdUntil = 0;
            sync();
        };

        // 4) 아이폰: 키보드에 가릴 칸을 누르면 문서를 먼저 올리고 직접 포커스(iOS 가 보이는 화면을 밀어 머리줄이 미끄러지지 않게)
        const ios: boolean = isIOS();
        let tapStart: { x: number; y: number } | null = null;
        const onTouchStart = (event: TouchEvent): void => {
            const touch: Touch | undefined = event.touches[0];
            tapStart = event.touches.length === 1 && touch ? { x: touch.clientX, y: touch.clientY } : null;
        };
        const onTouchEnd = (event: TouchEvent): void => {
            const start: { x: number; y: number } | null = tapStart;
            tapStart = null;
            if (!ios || !start || !event.cancelable) return;
            const touch: Touch | undefined = event.changedTouches[0];
            if (!touch || Math.abs(touch.clientX - start.x) > TAP_SLOP_PX || Math.abs(touch.clientY - start.y) > TAP_SLOP_PX) return;
            const field: Element | null = event.target instanceof Element ? event.target.closest('input, textarea') : null;
            if (!isTextEntry(field) || document.activeElement === field || !isRendered(field)) return;
            if (field.closest('[role="dialog"]') || body.dataset.scrollLock === 'true' || isSoftKeyboardUp()) return;
            // 자기 키보드 처리를 하는 입력줄(대화방·문의 대화)은 건드리지 않는다 — 여기서 먼저 포커스하면 그 입력줄의
            // onTouchEnd 가 "이미 포커스됨"으로 빠져 입력줄을 키보드 자리로 먼저 올리는 처리가 빠진다(흰 깜박임·늦게 따라옴)
            if (field.closest('[data-thread-composer], [data-inquiry-composer]')) return;
            // 화면에 고정된 줄(댓글 입력줄 등 position:fixed/sticky) 안 칸은 문서를 올려도 칸이 안 움직인다 — 올리면 본문만 위로 밀려
            // 글이 사라진 것처럼 보였다(2026-10-07 실기기 녹화, 속닥속닥 상세 댓글 입력줄). 그런 칸은 자기 시트·iOS 에 맡긴다
            if (inFixedLayer(field)) return;
            // 아이폰 글쓰기 화면은 문서를 묶고 #root 안에서 칸을 직접 맞춘다(components/common/write/useWriteKeyboardFrame) — 여기서 먼저
            // 포커스하면 그 훅이 "이미 포커스됨"으로 빠진다(지금은 문서가 묶여 결과가 같지만 등록 순서에 기대지 않게)
            if (body.dataset.writeFrame === 'true') return;
            const keyboardTop: number = window.innerHeight - Math.max(getLastKeyboardInset(), Math.round(window.innerHeight * IOS_KEYBOARD_RATIO));
            const rect: DOMRect = field.getBoundingClientRect();
            const limit: number = keyboardTop - PRE_RAISE_GAP_PX;
            if (rect.bottom <= limit) return;
            const { top: visibleTop, bottom: visibleBottom } = visibleBand();
            const barsBottom: number = obstructedTop(visibleTop, visibleBottom);
            // 칸이 키보드 위 자리보다 크면(긴 글 칸) 윗부분을 고정 줄 바로 아래에
            const delta: number = Math.min(rect.bottom - limit, rect.top - barsBottom - REVEAL_GAP_PX);
            if (delta <= 0) return;
            event.preventDefault();
            window.scrollBy({ top: delta, left: 0, behavior: 'auto' });
            field.focus({ preventScroll: true });
        };

        updateBaseline();
        keyboardWasUp = isSoftKeyboardUp();
        document.addEventListener('touchstart', onTouchStart, { capture: true, passive: true });
        document.addEventListener('touchend', onTouchEnd, { capture: true, passive: false });
        const vv: VisualViewport | null = window.visualViewport;
        document.addEventListener('focusin', onFocusIn);
        document.addEventListener('focusout', onFocusOut);
        document.addEventListener('pointerdown', onPointerDown, true);
        document.addEventListener('pointerup', onPointerEnd, true);
        document.addEventListener('pointercancel', onPointerEnd, true);
        document.addEventListener('visibilitychange', onHide);
        window.addEventListener('pagehide', onHide);
        window.addEventListener('resize', onViewportChange);
        vv?.addEventListener('resize', onViewportChange);
        vv?.addEventListener('scroll', onScrollWhilePending);
        window.addEventListener('scroll', onScrollWhilePending, { passive: true });
        return () => {
            document.removeEventListener('touchstart', onTouchStart, { capture: true });
            document.removeEventListener('touchend', onTouchEnd, { capture: true });
            document.removeEventListener('focusin', onFocusIn);
            document.removeEventListener('focusout', onFocusOut);
            document.removeEventListener('pointerdown', onPointerDown, true);
            document.removeEventListener('pointerup', onPointerEnd, true);
            document.removeEventListener('pointercancel', onPointerEnd, true);
            document.removeEventListener('visibilitychange', onHide);
            window.removeEventListener('pagehide', onHide);
            window.removeEventListener('resize', onViewportChange);
            vv?.removeEventListener('resize', onViewportChange);
            vv?.removeEventListener('scroll', onScrollWhilePending);
            window.removeEventListener('scroll', onScrollWhilePending);
            window.clearTimeout(timer);
            window.clearTimeout(revealTimer);
            if (frame) window.cancelAnimationFrame(frame);
            observer?.disconnect();
            delete body.dataset.inputFocused;
            delete body.dataset.keyboardUp;
        };
    }, []);
};
